import React from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Bus,
  Utensils,
  Library,
  Clock,
  AlertTriangle,
  CheckCircle,
  BarChart3,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./Dashboard.css";

const Dashboard = () => {
  const { user, hasPermission } = useAuth();

  const allStats = [
    {
      title: "Active Loans",
      value: "247",
      change: "+12%",
      icon: BookOpen,
      color: "blue",
      trend: "up",
      permission: "library_manage",
    },
    {
      title: "Overdue Books",
      value: "23",
      change: "-5%",
      icon: AlertTriangle,
      color: "red",
      trend: "down",
      permission: "library_manage",
    },
    {
      title: "My Active Loans",
      value: "3",
      change: "+1",
      icon: BookOpen,
      color: "blue",
      trend: "up",
      permission: "library_view_own",
    },
    {
      title: "My Overdue Books",
      value: "0",
      change: "0",
      icon: AlertTriangle,
      color: "red",
      trend: "down",
      permission: "library_view_own",
    },
    {
      title: "Child's Loans",
      value: "2",
      change: "+1",
      icon: BookOpen,
      color: "blue",
      trend: "up",
      permission: "library_view_child",
    },
    {
      title: "Buses Active",
      value: "8",
      change: "100%",
      icon: Bus,
      color: "green",
      trend: "up",
      permission: "transportation_manage",
    },
    {
      title: "Child's Bus Status",
      value: "On Time",
      change: "Good",
      icon: Bus,
      color: "green",
      trend: "up",
      permission: "transportation_view_child",
    },
    {
      title: "Cafeteria Sales",
      value: "$2,340",
      change: "+8%",
      icon: Utensils,
      color: "purple",
      trend: "up",
      permission: "cafeteria_manage",
    },
    {
      title: "My Balance",
      value: "$45.50",
      change: "-$5.00",
      icon: Utensils,
      color: "purple",
      trend: "down",
      permission: "cafeteria_view_own",
    },
    {
      title: "Child's Balance",
      value: "$23.75",
      change: "-$2.50",
      icon: Utensils,
      color: "purple",
      trend: "down",
      permission: "cafeteria_view_child",
    },
    {
      title: "E-Books Available",
      value: "1,247",
      change: "+15",
      icon: Library,
      color: "orange",
      trend: "up",
      permission: "e_library_access",
    },
    {
      title: "My Downloads",
      value: "12",
      change: "+3",
      icon: Library,
      color: "orange",
      trend: "up",
      permission: "e_library_download",
    },
  ];

  // Filter stats based on user permissions
  const stats = allStats.filter((stat) => hasPermission(stat.permission));

  const allRecentActivities = [
    {
      id: 1,
      type: "borrow",
      user: "Emma Wilson",
      book: "Advanced Mathematics",
      time: "2 minutes ago",
      status: "success",
      permission: "library_manage",
    },
    {
      id: 2,
      type: "return",
      user: "Michael Chen",
      book: "Physics Fundamentals",
      time: "5 minutes ago",
      status: "success",
      permission: "library_manage",
    },
    {
      id: 3,
      type: "overdue",
      user: "Sarah Johnson",
      book: "Chemistry Lab Manual",
      time: "1 hour ago",
      status: "warning",
      permission: "library_manage",
    },
    {
      id: 4,
      type: "bus",
      user: "Bus Route 3",
      action: "Arrived at Main Gate",
      time: "15 minutes ago",
      status: "info",
      permission: "transportation_manage",
    },
    {
      id: 5,
      type: "borrow",
      user: "You",
      book: "World History",
      time: "1 hour ago",
      status: "success",
      permission: "library_view_own",
    },
    {
      id: 6,
      type: "download",
      user: "You",
      book: "Physics Textbook PDF",
      time: "2 hours ago",
      status: "success",
      permission: "e_library_download",
    },
    {
      id: 7,
      type: "purchase",
      user: "You",
      book: "Lunch - $5.50",
      time: "3 hours ago",
      status: "success",
      permission: "cafeteria_view_own",
    },
    {
      id: 8,
      type: "child_borrow",
      user: "Emma Wilson (Your Child)",
      book: "Chemistry Lab Manual",
      time: "4 hours ago",
      status: "success",
      permission: "library_view_child",
    },
    {
      id: 9,
      type: "child_bus",
      user: "Emma Wilson (Your Child)",
      action: "Boarded Bus Route 3",
      time: "6 hours ago",
      status: "success",
      permission: "transportation_view_child",
    },
  ];

  // Filter activities based on user permissions
  const recentActivities = allRecentActivities.filter((activity) =>
    hasPermission(activity.permission)
  );

  const allQuickActions = [
    {
      title: "Library Management",
      description: "Manage books, loans, and cataloging",
      icon: BookOpen,
      path: "/library",
      color: "blue",
      permission: "library_manage",
    },
    {
      title: "Book Requests",
      description: "Request books from the library",
      icon: BookOpen,
      path: "/library/requests",
      color: "blue",
      permission: "library_request",
    },
    {
      title: "Borrow Books",
      description: "Borrow books from the library",
      icon: BookOpen,
      path: "/library/borrow",
      color: "blue",
      permission: "library_borrow",
    },
    {
      title: "Transportation",
      description: "Track buses and manage routes",
      icon: Bus,
      path: "/transportation",
      color: "green",
      permission: "transportation_manage",
    },
    {
      title: "Cafeteria Management",
      description: "Manage payments and meal plans",
      icon: Utensils,
      path: "/cafeteria",
      color: "purple",
      permission: "cafeteria_manage",
    },
    {
      title: "Make Purchase",
      description: "Purchase meals and snacks",
      icon: Utensils,
      path: "/cafeteria/purchase",
      color: "purple",
      permission: "cafeteria_purchase",
    },
    {
      title: "Cafeteria Controls",
      description: "Control child's cafeteria spending",
      icon: Utensils,
      path: "/cafeteria/controls",
      color: "purple",
      permission: "cafeteria_controls",
    },
    {
      title: "E-Library",
      description: "Digital books and resources",
      icon: Library,
      path: "/e-library",
      color: "orange",
      permission: "e_library_access",
    },
    {
      title: "My Downloads",
      description: "View your downloaded materials",
      icon: Library,
      path: "/e-library/downloads",
      color: "orange",
      permission: "e_library_download",
    },
    {
      title: "Child Monitoring",
      description: "Monitor your child's activities",
      icon: BookOpen,
      path: "/child-monitoring",
      color: "teal",
      permission: "library_view_child",
    },
    {
      title: "Admin Panel",
      description: "System administration and settings",
      icon: BarChart3,
      path: "/admin",
      color: "red",
      permission: "admin",
    },
  ];

  // Filter quick actions based on user permissions
  const quickActions = allQuickActions.filter((action) =>
    hasPermission(action.permission)
  );

  const getActivityIcon = (type) => {
    switch (type) {
      case "borrow":
        return <BookOpen size={16} className="text-blue-500" />;
      case "return":
        return <CheckCircle size={16} className="text-green-500" />;
      case "overdue":
        return <AlertTriangle size={16} className="text-red-500" />;
      case "bus":
        return <Bus size={16} className="text-blue-500" />;
      case "download":
        return <Library size={16} className="text-orange-500" />;
      case "purchase":
        return <Utensils size={16} className="text-purple-500" />;
      case "child_borrow":
        return <BookOpen size={16} className="text-teal-500" />;
      case "child_bus":
        return <Bus size={16} className="text-teal-500" />;
      default:
        return <Clock size={16} className="text-gray-500" />;
    }
  };

  const getActivityText = (activity) => {
    switch (activity.type) {
      case "borrow":
        return `${activity.user} borrowed "${activity.book}"`;
      case "return":
        return `${activity.user} returned "${activity.book}"`;
      case "overdue":
        return `${activity.user} has overdue book "${activity.book}"`;
      case "bus":
        return `${activity.user}: ${activity.action}`;
      case "download":
        return `${activity.user} downloaded "${activity.book}"`;
      case "purchase":
        return `${activity.user} purchased ${activity.book}`;
      case "child_borrow":
        return `${activity.user} borrowed "${activity.book}"`;
      case "child_bus":
        return `${activity.user} ${activity.action}`;
      default:
        return "Unknown activity";
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Welcome back, {user?.name}!</h1>
        <p>Here's what's happening in your school today</p>
      </div>

      <div className="stats-grid">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`stat-card stat-${stat.color}`}>
              <div className="stat-icon">
                <Icon size={24} />
              </div>
              <div className="stat-content">
                <h3>{stat.value}</h3>
                <p>{stat.title}</p>
                <span className={`stat-change stat-change-${stat.trend}`}>
                  {stat.change}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Recent Activity</h3>
            <button className="btn btn-outline">View All</button>
          </div>
          <div className="activity-list">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className="activity-icon">
                  {getActivityIcon(activity.type)}
                </div>
                <div className="activity-content">
                  <p className="activity-text">{getActivityText(activity)}</p>
                  <span className="activity-time">{activity.time}</span>
                </div>
                <div
                  className={`activity-status status-${activity.status}`}
                ></div>
              </div>
            ))}
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h3>Quick Actions</h3>
          </div>
          <div className="quick-actions">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Link
                  key={index}
                  to={action.path}
                  className={`quick-action action-${action.color}`}
                >
                  <div className="action-icon">
                    <Icon size={24} />
                  </div>
                  <div className="action-content">
                    <h4>{action.title}</h4>
                    <p>{action.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="card-header">
            <h3>System Overview</h3>
            <BarChart3 size={20} className="text-gray-500" />
          </div>
          <div className="system-overview">
            {hasPermission("library_manage") && (
              <div className="overview-item">
                <div className="overview-label">Total Books</div>
                <div className="overview-value">12,547</div>
              </div>
            )}
            {hasPermission("admin") && (
              <div className="overview-item">
                <div className="overview-label">Active Students</div>
                <div className="overview-value">1,234</div>
              </div>
            )}
            {hasPermission("transportation_manage") && (
              <div className="overview-item">
                <div className="overview-label">Bus Routes</div>
                <div className="overview-value">15</div>
              </div>
            )}
            {hasPermission("cafeteria_manage") && (
              <div className="overview-item">
                <div className="overview-label">Daily Transactions</div>
                <div className="overview-value">89</div>
              </div>
            )}
            {hasPermission("e_library_access") && (
              <div className="overview-item">
                <div className="overview-label">E-Books Available</div>
                <div className="overview-value">1,247</div>
              </div>
            )}
            {hasPermission("library_view_own") && (
              <div className="overview-item">
                <div className="overview-label">My Active Loans</div>
                <div className="overview-value">3</div>
              </div>
            )}
            {hasPermission("library_view_child") && (
              <div className="overview-item">
                <div className="overview-label">Child's Active Loans</div>
                <div className="overview-value">2</div>
              </div>
            )}
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h3>Alerts & Notifications</h3>
            <AlertTriangle size={20} className="text-red-500" />
          </div>
          <div className="alerts-list">
            {hasPermission("transportation_manage") && (
              <div className="alert-item alert-warning">
                <AlertTriangle size={16} />
                <span>3 buses running late (Route 2, 5, 8)</span>
              </div>
            )}
            {hasPermission("library_manage") && (
              <div className="alert-item alert-info">
                <Clock size={16} />
                <span>Library closing in 30 minutes</span>
              </div>
            )}
            {hasPermission("admin") && (
              <div className="alert-item alert-success">
                <CheckCircle size={16} />
                <span>All systems operational</span>
              </div>
            )}
            {hasPermission("library_view_own") && (
              <div className="alert-item alert-info">
                <BookOpen size={16} />
                <span>You have 2 books due tomorrow</span>
              </div>
            )}
            {hasPermission("library_view_child") && (
              <div className="alert-item alert-warning">
                <BookOpen size={16} />
                <span>Your child has 1 overdue book</span>
              </div>
            )}
            {hasPermission("cafeteria_view_own") && (
              <div className="alert-item alert-info">
                <Utensils size={16} />
                <span>Your balance is low: $5.50</span>
              </div>
            )}
            {hasPermission("cafeteria_view_child") && (
              <div className="alert-item alert-info">
                <Utensils size={16} />
                <span>Child's balance: $23.75</span>
              </div>
            )}
            {hasPermission("transportation_view_child") && (
              <div className="alert-item alert-success">
                <Bus size={16} />
                <span>Child's bus is on time</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
